import { getBanksData, getCountriesAllowedToOperateData } from '../../api/payments.api'
import type { Bank, UserSessionType } from '../../types/user.types'
import type { ProfileDto } from '../dtos/user-profile.dto'
import { mapSuiteProfileToPayUserSession } from '../mappers/suite-profile.mapper'
import { resolvePayAccessTokenForMicroservices, resolvePayUserSessionFromRefreshToken } from './pay-auth-bridge.service'
import { clearSuiteAccessTokenCache } from './suite-access-token.service'
import { logInfo, logWarn } from '../utils/sso-logger'

type Deps = {
  profile: ProfileDto
  setUserSessionData: (user: UserSessionType) => void
  setCurrentBanksData?: (banks: Bank[]) => void
  updateSyncStatus?: (sync: boolean) => void
  clearLegacyPayToken?: () => void
  activateAuth?: (token: { token: string }) => void
}

export type SuiteDashboardBootstrapResult =
  | { ok: true; payUserSession: UserSessionType }
  | { ok: false; error: unknown }

/**
 * After suite profile is valid, bridge Identity → Pay auth, then hydrate dashboard data.
 */
export async function bootstrapSuiteDashboardSession(deps: Deps): Promise<SuiteDashboardBootstrapResult> {
  logInfo('bootstrapSuiteDashboardSession — start', {
    profileUuid: deps.profile.uuid,
    profileEmail: deps.profile.email
  })

  globalThis.sessionStorage.removeItem('suiteDashboardReady')

  try {
    clearSuiteAccessTokenCache()

    deps.clearLegacyPayToken?.()

    await resolvePayAccessTokenForMicroservices(deps.activateAuth)

    const suiteUserSession = mapSuiteProfileToPayUserSession(deps.profile)
    const payUserSessionFromToken = resolvePayUserSessionFromRefreshToken()
    const payUserSession: UserSessionType = payUserSessionFromToken
      ? {
          ...suiteUserSession,
          ...payUserSessionFromToken,
          firstName: payUserSessionFromToken.firstName || suiteUserSession.firstName,
          lastName: payUserSessionFromToken.lastName || suiteUserSession.lastName
        }
      : suiteUserSession

    deps.setUserSessionData(payUserSession)

    const [banksResponse, countriesResponse] = await Promise.all([getBanksData(), getCountriesAllowedToOperateData()])

    globalThis.localStorage.setItem('_CAO', JSON.stringify(countriesResponse.data))
    deps.setCurrentBanksData?.(banksResponse.data)
    deps.updateSyncStatus?.(true)
    globalThis.sessionStorage.setItem('suiteDashboardReady', 'true')
    globalThis.dispatchEvent(new CustomEvent('suite-dashboard-ready'))

    logInfo('bootstrapSuiteDashboardSession — done', {
      payUserId: payUserSession._id,
      belongsTo: payUserSession.belongsTo,
      banksCount: banksResponse.data?.length,
      countriesCount: countriesResponse.data?.length
    })

    return { ok: true, payUserSession }
  } catch (error) {
    logWarn('bootstrapSuiteDashboardSession — failed', { error })
    globalThis.sessionStorage.removeItem('suiteDashboardReady')
    return { ok: false, error }
  }
}
